import React, { useState, useRef, useEffect } from 'react';
import { Mail, Send, AlertCircle, ChevronDown } from 'lucide-react';
import emailjs from '@emailjs/browser';

// EmailJS Configuration from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Company Info from environment variables
const companyInfo = {
  email: process.env.REACT_APP_COMPANY_EMAIL || 'contato@essolucoestec.com.br'
};

// Departamentos disponíveis
const departments = [
  { 
    value: 'comercial', 
    label: 'Comercial', 
    description: 'Novos clientes, orçamentos, propostas' 
  },
  { 
    value: 'suporte', 
    label: 'Suporte Técnico', 
    description: 'Clientes com problemas ou dúvidas técnicas' 
  },
  { 
    value: 'desenvolvimento', 
    label: 'Desenvolvimento', 
    description: 'Discussões sobre projetos, customizações' 
  },
  { 
    value: 'financeiro', 
    label: 'Financeiro', 
    description: 'Pagamentos, notas fiscais, boletos' 
  },
  { 
    value: 'parcerias', 
    label: 'Parcerias', 
    description: 'Empresas interessadas em parcerias' 
  },
  { 
    value: 'trabalhe-conosco', 
    label: 'Trabalhe Conosco', 
    description: 'Candidatos a vagas na empresa' 
  },
  { 
    value: 'outros', 
    label: 'Outros', 
    description: 'Outros assuntos' 
  }
];

// Validação de email
const validateEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

// Máscara de telefone brasileiro (celular e fixo)
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  const limited = numbers.slice(0, 11);
  
  if (limited.length <= 2) {
    return limited.length ? `(${limited}` : '';
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  } else if (limited.length <= 10) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
  } else {
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  }
};

// Validação de telefone (mínimo 10 dígitos - fixo)
const validatePhone = (phone) => {
  const numbers = phone.replace(/\D/g, '');
  return numbers.length >= 10;
};

// Custom Select Component
const CustomSelect = ({ value, onChange, onBlur, error, touched }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  
  const selectedDepartment = departments.find(d => d.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (departmentValue) => {
    onChange({ target: { name: 'department', value: departmentValue } });
    setIsOpen(false);
  };

  const hasError = touched && error;

  return (
    <div ref={selectRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={onBlur}
        className={`w-full px-4 py-3 bg-white/5 border text-left flex items-center justify-between transition-colors ${
          hasError 
            ? 'border-red-500' 
            : isOpen 
              ? 'border-cyan-400' 
              : 'border-white/10 hover:border-white/20'
        }`}
      >
        {selectedDepartment ? (
          <div>
            <span className="text-white font-medium">{selectedDepartment.label}</span>
            <span className="text-white/50 text-sm ml-2">- {selectedDepartment.description}</span>
          </div>
        ) : (
          <span className="text-white/30">Selecione um departamento</span>
        )}
        <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-white/10 max-h-80 overflow-y-auto">
          {departments.map((dept) => (
            <button
              key={dept.value}
              type="button"
              onClick={() => handleSelect(dept.value)}
              className={`w-full px-4 py-3 text-left hover:bg-cyan-400/10 transition-colors border-b border-white/5 last:border-b-0 ${
                value === dept.value ? 'bg-cyan-400/10' : ''
              }`}
            >
              <div className="text-white font-semibold">{dept.label}</div>
              <div className="text-white/50 text-sm">{dept.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhone(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
    validateField(name);
  };

  const validateField = (fieldName) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'name':
        if (!formData.name.trim()) {
          newErrors.name = 'Nome é obrigatório';
        } else if (formData.name.trim().length < 3) {
          newErrors.name = 'Nome deve ter pelo menos 3 caracteres';
        } else {
          delete newErrors.name;
        }
        break;
        
      case 'email':
        if (!formData.email.trim()) {
          newErrors.email = 'E-mail é obrigatório';
        } else if (!validateEmail(formData.email)) {
          newErrors.email = 'E-mail inválido';
        } else {
          delete newErrors.email;
        }
        break;
        
      case 'phone':
        if (formData.phone && !validatePhone(formData.phone)) {
          newErrors.phone = 'Telefone incompleto';
        } else {
          delete newErrors.phone;
        }
        break;

      case 'department':
        if (!formData.department) {
          newErrors.department = 'Selecione um departamento';
        } else {
          delete newErrors.department;
        }
        break;

      case 'subject':
        if (!formData.subject.trim()) {
          newErrors.subject = 'Assunto é obrigatório';
        } else if (formData.subject.trim().length < 5) {
          newErrors.subject = 'Assunto deve ter pelo menos 5 caracteres';
        } else {
          delete newErrors.subject;
        }
        break;
        
      case 'message':
        if (!formData.message.trim()) {
          newErrors.message = 'Mensagem é obrigatória';
        } else if (formData.message.trim().length < 10) {
          newErrors.message = 'Mensagem deve ter pelo menos 10 caracteres';
        } else {
          delete newErrors.message;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Nome deve ter pelo menos 3 caracteres';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Telefone incompleto';
    }

    if (!formData.department) {
      newErrors.department = 'Selecione um departamento';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Assunto é obrigatório';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Assunto deve ter pelo menos 5 caracteres';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Mensagem deve ter pelo menos 10 caracteres';
    }
    
    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true, department: true, subject: true, message: true });
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    const selectedDepartment = departments.find(d => d.value === formData.department);
    
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Não informado',
          department: selectedDepartment ? selectedDepartment.label : 'Não especificado',
          subject: formData.subject,
          message: formData.message,
          title: `[${selectedDepartment?.label || 'Contato'}] ${formData.subject}`
        },
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', department: '', subject: '', message: '' });
      setTouched({});
      setErrors({});
      
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Erro ao enviar email:', err);
      setSubmitError('Erro ao enviar mensagem. Tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputClassName = (fieldName) => {
    const baseClass = "w-full px-4 py-3 bg-white/5 border text-white placeholder-white/30 focus:outline-none transition-colors";
    const hasError = touched[fieldName] && errors[fieldName];
    
    return `${baseClass} ${hasError ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-cyan-400'}`;
  };

  return (
    <section id="contato" className="relative py-24 bg-black">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute right-0 top-0 w-96 h-96 bg-cyan-400/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/20 mb-6">
            <span className="text-cyan-400 text-sm font-medium">Contato</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Vamos <span className="text-cyan-400">conversar?</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Entre em contato conosco e descubra como podemos ajudar sua empresa a crescer com tecnologia.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="p-8 bg-white/[0.02] border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-6">Informações de Contato</h3>
              
              <div className="space-y-6">
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-400/10 border border-cyan-400/20 group-hover:bg-cyan-400 group-hover:border-cyan-400 transition-all duration-300">
                    <Mail className="w-5 h-5 text-cyan-400 group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <div className="text-white/50 text-sm">E-mail</div>
                    <div className="text-white group-hover:text-cyan-400 transition-colors">
                      {companyInfo.email}
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="p-8 bg-white/[0.02] border border-white/10">
            <h3 className="text-xl font-semibold text-white mb-6">Envie uma mensagem</h3>
            
            {submitted ? (
              <div className="p-6 bg-cyan-400/10 border border-cyan-400/30 text-center">
                <div className="text-cyan-400 font-semibold text-lg mb-2">Mensagem enviada!</div>
                <p className="text-white/60">Entraremos em contato em breve.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {submitError && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {submitError}
                  </div>
                )}
                
                {/* Nome */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Nome <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={getInputClassName('name')}
                    placeholder="Seu nome completo"
                  />
                  {touched.name && errors.name && (
                    <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email e Telefone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">
                      E-mail <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={getInputClassName('email')}
                      placeholder="seu@email.com"
                    />
                    {touched.email && errors.email && (
                      <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Telefone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={getInputClassName('phone')}
                      placeholder="(00) 00000-0000"
                    />
                    {touched.phone && errors.phone && (
                      <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Departamento */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Departamento <span className="text-red-400">*</span>
                  </label>
                  <CustomSelect
                    value={formData.department}
                    onChange={handleChange}
                    onBlur={() => {
                      setTouched({ ...touched, department: true });
                      validateField('department');
                    }}
                    error={errors.department}
                    touched={touched.department}
                  />
                  {touched.department && errors.department && (
                    <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.department}
                    </p>
                  )}
                </div>

                {/* Assunto */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Assunto <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={getInputClassName('subject')}
                    placeholder="Qual o assunto da mensagem?"
                  />
                  {touched.subject && errors.subject && (
                    <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.subject}
                    </p>
                  )}
                </div>

                {/* Mensagem */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    Mensagem <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={`${getInputClassName('message')} resize-none`}
                    placeholder="Conte-nos sobre seu projeto ou dúvida..."
                  />
                  {touched.message && errors.message && (
                    <p className="mt-1 text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-cyan-400 text-black font-medium hover:bg-cyan-400/10 hover:text-cyan-400 transition-all duration-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
